import { ChangeEvent, memo, useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Content } from 'src/contexts/ContentContext';
import { Switch as SwitchV1 } from 'src/core/interim/Switch';
import { vars } from 'src/core/themes';
import { Stack, Switch } from 'src/core/ui';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TooltipPopover } from 'src/core/ui/TooltipPopover';
import { useHasListingPricePermissions } from 'src/modals/ListingDetails/components/useHasListingPricePermissions';
import { WarningOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Event, Listing } from 'src/WebApiController';

import { ListingPricingInput } from './ListingPriceForm.types';
import { useCellDisabled, useFieldIsSubmitting } from './useCellState';

type AutoPricingToggleCellProps = {
  listing: Listing;
  editable?: boolean;
  disabled?: boolean;
  event: Event;
  isV2Variant?: boolean;
};

const AutoPricingToggleCellRaw = ({
  listing,
  editable,
  disabled,
  event,
  isV2Variant = false,
}: AutoPricingToggleCellProps) => {
  const field = 'autoPricingEnabled';
  const { setValue, watch, getFieldState, formState } =
    useFormContext<ListingPricingInput>();

  const { canSetPrice, canAutoPrice } = useHasListingPricePermissions(listing);

  const autoPricingEnabled = watch('autoPricingEnabled');
  const rowIndex = watch('rowIndex');

  const fieldValue = watch(field);

  const fieldIsSubmitting = useFieldIsSubmitting();

  const isDisabled = useCellDisabled(
    listing,
    canSetPrice,
    !disabled,
    event.virtId,
    disabled
  );

  const fieldError = getFieldState(field, formState)?.error?.message;

  const fieldHasChanged =
    formState.defaultValues?.[field] !== fieldValue && !fieldIsSubmitting;

  const submit = useCallback(() => {
    if (fieldError) {
      return;
    }
    if (fieldHasChanged) {
      // Only submit if Enter is hit in the box that has changes
      setValue('isSubmittingPricingSettings', true);
      setValue('isSubmittingRowIndex', rowIndex);
    }
  }, [fieldError, fieldHasChanged, setValue, rowIndex]);

  useEffect(() => {
    submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPricingEnabled]);

  const content = editable ? (
    !canAutoPrice ? (
      <Stack direction="row" gap="s" alignItems="center">
        <TooltipPopover
          variant="link"
          contentVariant="dark"
          triggerContent={
            <WarningOutlineIcon
              fill={'#FD9927'}
              size={vars.iconSize.m}
              withHoverEffect
            />
          }
        >
          <Content id={ContentId.NoAutoPricing_MissingPermission} />
        </TooltipPopover>
        <Content id={ContentId.Off} />
      </Stack>
    ) : (
      <Stack direction="row" gap="s" alignItems="center">
        {isV2Variant ? (
          <Switch
            checked={autoPricingEnabled ?? false}
            disabled={isDisabled}
            onCheckedChange={(checked) => {
              if (checked !== autoPricingEnabled) {
                setValue('autoPricingEnabled', checked);
              }
            }}
          />
        ) : (
          <>
            <SwitchV1
              checked={autoPricingEnabled ?? false}
              disabled={isDisabled}
              onClick={(e: ChangeEvent<HTMLElement>) => e.stopPropagation()}
              onChange={(e) => {
                const isChecked = e.target.checked;
                if (isChecked !== autoPricingEnabled) {
                  setValue('autoPricingEnabled', isChecked);
                }
              }}
            />
            <Content id={autoPricingEnabled ? ContentId.On : ContentId.Off} />
          </>
        )}
      </Stack>
    )
  ) : null;

  return isV2Variant ? (
    content
  ) : (
    <TableCellDiv align="right" showTooltip={false}>
      {content}
    </TableCellDiv>
  );
};

export const AutoPricingToggleCell = memo<AutoPricingToggleCellProps>(
  AutoPricingToggleCellRaw
);
